import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * Tracker chart tooltips that stay on screen.
 *
 * Two shapes share one placement rule:
 *  - ViewportChartTooltip, a Recharts `content` component for line charts. Recharts places its own
 *    tooltip at the cursor, so on a chart with a long series list the card ran off the bottom of the
 *    page. This one is vertically centred on the chart and clamped to the viewport.
 *  - FloatingTooltip + BreakdownCard, for the hand-drawn stacked bars on Leads & Partners: a card
 *    listing every segment in the hovered bar.
 *
 * Both render through a portal with fixed positioning, so the card is never clipped by a panel's
 * overflow and never shifts the layout. The look is the tracker's dark tooltip card (StageTrendChart,
 * SalesCharts' TooltipCard): gray-900 ground, gray-700 border, rounded.
 *
 * A fixed card is placed once, from where its anchor was when it opened, so it must close the
 * moment that position goes stale: on any scroll (the page or any panel), a resize, the window
 * losing focus, and a touch or pen tap outside the chart (touch never sends a mouse leave). A card
 * left open on scroll floated over unrelated content (founder, 5 Oct 2026).
 */

export const TOOLTIP_CARD = 'rounded-lg border border-gray-700 bg-gray-900 p-3 text-xs text-white shadow-xl';

/** The hovered thing, in viewport coordinates: its horizontal extent and the band to centre on. */
export interface TooltipAnchor {
  left: number;
  right: number;
  top: number;
  height: number;
}

export interface TooltipPlacementInput {
  anchor: TooltipAnchor;
  cardWidth: number;
  cardHeight: number;
  viewportWidth: number;
  viewportHeight: number;
  /** Space between the anchor and the card. */
  gap?: number;
  /** Space kept between the card and the viewport edge. */
  margin?: number;
}

/**
 * Where the card goes. Vertically centred on the anchor's band, then clamped so it sits wholly
 * inside the viewport (a card taller than the viewport pins to the top). Horizontally just right of
 * the anchor, flipping to its left when it would cross the right edge.
 */
export function placeTooltip({
  anchor,
  cardWidth,
  cardHeight,
  viewportWidth,
  viewportHeight,
  gap = 12,
  margin = 8,
}: TooltipPlacementInput): { left: number; top: number; flipped: boolean } {
  let top = anchor.top + anchor.height / 2 - cardHeight / 2;
  top = Math.max(margin, Math.min(top, viewportHeight - margin - cardHeight));

  let left = anchor.right + gap;
  let flipped = false;
  if (left + cardWidth > viewportWidth - margin) {
    left = anchor.left - gap - cardWidth;
    flipped = true;
  }
  left = Math.max(margin, Math.min(left, viewportWidth - margin - cardWidth));
  return { left: Math.round(left), top: Math.round(top), flipped };
}

/**
 * A card fixed to the viewport beside `anchor`, or nothing when `anchor` is null. It measures
 * itself before paint and places itself with placeTooltip, so it never flashes at a wrong spot.
 */
export const FloatingTooltip: React.FC<{
  anchor: TooltipAnchor | null;
  id?: string;
  /** Called when the open card goes stale: scroll, resize, window blur, or a tap outside `within`. */
  onDismiss?: () => void;
  /** The chart the card belongs to; a touch or pen tap inside it does not dismiss. */
  within?: React.RefObject<Element>;
  children: React.ReactNode;
}> = ({ anchor, id, onDismiss, within, children }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  const dismissRef = useRef(onDismiss);
  dismissRef.current = onDismiss;
  const open = anchor !== null;

  useEffect(() => {
    if (!open) return undefined;
    const dismiss = () => dismissRef.current?.();
    const onPointerDown = (e: PointerEvent) => {
      // A mouse has mouse leave; touch and pen do not, so a tap elsewhere is their leave.
      if (e.pointerType === 'mouse') return;
      if (within?.current && e.target instanceof Node && within.current.contains(e.target)) return;
      dismiss();
    };
    // Capture, so a scroll inside any panel or the app's main scroller is seen, not only the window's.
    window.addEventListener('scroll', dismiss, { capture: true, passive: true });
    window.addEventListener('resize', dismiss);
    window.addEventListener('blur', dismiss);
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => {
      window.removeEventListener('scroll', dismiss, { capture: true });
      window.removeEventListener('resize', dismiss);
      window.removeEventListener('blur', dismiss);
      document.removeEventListener('pointerdown', onPointerDown, true);
    };
  }, [open, within]);

  useLayoutEffect(() => {
    if (!anchor || !ref.current) {
      if (pos) setPos(null);
      return;
    }
    const rect = ref.current.getBoundingClientRect();
    const next = placeTooltip({
      anchor,
      cardWidth: rect.width,
      cardHeight: rect.height,
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
    });
    if (!pos || pos.left !== next.left || pos.top !== next.top) setPos({ left: next.left, top: next.top });
  });

  if (!anchor || typeof document === 'undefined') return null;
  return createPortal(
    <div
      ref={ref}
      id={id}
      role="tooltip"
      className="pointer-events-none fixed z-50 overflow-hidden"
      style={{
        left: pos?.left ?? 0,
        top: pos?.top ?? 0,
        maxHeight: 'calc(100vh - 1rem)',
        visibility: pos ? 'visible' : 'hidden',
      }}
    >
      {children}
    </div>,
    document.body,
  );
};

/** The anchor for an element: its own box, centred on its own height. */
export const anchorOf = (el: Element, band?: Element | null): TooltipAnchor => {
  const r = el.getBoundingClientRect();
  const b = band ? band.getBoundingClientRect() : r;
  return { left: r.left, right: r.right, top: b.top, height: b.height };
};

/** One series in a Recharts tooltip payload; only what the card reads. */
interface ChartPayloadItem {
  name?: string;
  value?: number | string;
  color?: string;
  dataKey?: string | number;
}

/**
 * Recharts `content` for a line chart: the card next to the hovered x, centred on the chart's
 * height, clamped to the viewport. `containerRef` is the element the chart fills (the
 * ResponsiveContainer's parent); Recharts' `coordinate` is relative to it.
 */
export const ViewportChartTooltip: React.FC<{
  containerRef: React.RefObject<HTMLElement>;
  format?: (value: number | string) => string;
  active?: boolean;
  payload?: ChartPayloadItem[];
  label?: React.ReactNode;
  coordinate?: { x: number; y: number };
}> = ({ containerRef, format, active, payload, label, coordinate }) => {
  // Recharts keeps its tooltip active until the mouse leaves the chart, so a scroll under a still
  // mouse would leave the card where the chart used to be. A dismissal suppresses the card until the
  // pointer next moves over the chart (Recharts then re-reads the point under it), or the hovered
  // point changes.
  const [dismissed, setDismissed] = useState<string | null>(null);
  const pointKey = coordinate ? `${coordinate.x}|${String(label)}` : null;
  const suppressed = dismissed !== null && pointKey === dismissed;
  useEffect(() => {
    // A new hovered point ends the suppression, so coming back to the old point later shows it.
    if (dismissed !== null && pointKey !== dismissed) setDismissed(null);
  }, [dismissed, pointKey]);
  useEffect(() => {
    const target = containerRef.current;
    if (dismissed === null || !target) return undefined;
    const resume = () => setDismissed(null);
    target.addEventListener('pointermove', resume, { passive: true });
    return () => target.removeEventListener('pointermove', resume);
  }, [dismissed, containerRef]);
  const el = containerRef.current;
  let anchor: TooltipAnchor | null = null;
  if (active && payload && payload.length > 0 && el && coordinate && !suppressed) {
    const r = el.getBoundingClientRect();
    const x = r.left + coordinate.x;
    anchor = { left: x, right: x, top: r.top, height: r.height };
  }
  return (
    <FloatingTooltip anchor={anchor} onDismiss={() => setDismissed(pointKey ?? '')} within={containerRef}>
      {anchor && payload && (
        <div className={TOOLTIP_CARD}>
          {label !== undefined && label !== '' && <p className="mb-1 font-bold">{label}</p>}
          {payload.map((p) => (
            <p key={String(p.dataKey ?? p.name)} style={{ color: p.color }}>
              {p.name}: <span className="tabular-nums">{format && p.value !== undefined ? format(p.value) : p.value}</span>
            </p>
          ))}
        </div>
      )}
    </FloatingTooltip>
  );
};

export interface BreakdownRow {
  key: string;
  name: string;
  colour: string;
  /** Already formatted in the panel's unit. */
  value: string;
  /** Share of the bar total, already formatted. */
  share: string;
}

/** Every segment of one stacked bar: swatch, name, value, share, the hovered one highlighted. */
export const BreakdownCard: React.FC<{
  title: string;
  rows: BreakdownRow[];
  activeKey: string | null;
  total: string;
}> = ({ title, rows, activeKey, total }) => (
  <div className={`${TOOLTIP_CARD} min-w-[13rem] max-w-[20rem]`} data-testid="breakdown-tooltip">
    <p className="mb-1.5 font-bold">{title}</p>
    <ul className="space-y-0.5">
      {rows.map((r) => {
        const active = r.key === activeKey;
        return (
          <li
            key={r.key}
            data-active={active || undefined}
            className={`-mx-1 flex items-center gap-2 rounded px-1 py-0.5 ${active ? 'bg-blue-500/15 font-semibold text-white ring-1 ring-inset ring-blue-500/40' : 'text-gray-300'}`}
          >
            <span aria-hidden="true" className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ background: r.colour }} />
            <span className="min-w-0 flex-1 truncate">{r.name}</span>
            <span className="tabular-nums">{r.value}</span>
            <span className="w-9 text-right tabular-nums text-gray-400">{r.share}</span>
          </li>
        );
      })}
    </ul>
    <div className="mt-1.5 flex items-center justify-between gap-3 border-t border-gray-700 pt-1.5 font-semibold">
      <span>Total</span>
      <span className="tabular-nums">{total}</span>
    </div>
  </div>
);
